// src/components/common/TokenPrice.js
import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import {
    UNISWAP_PAIR_ADDRESS,
    WETH_ADDRESS,
    NETWORK_CONFIG
} from '@/constants/contract';

// GR33D-SIMPLIFIED-V2: [2025-02-26] - Direct contract interaction pattern

const TokenPrice = () => {
    // Use a reference to avoid multiple renders
    const [price, setPrice] = useState("");
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const fetchingRef = useRef(false);
    const cacheTimeRef = useRef(0);
    const cachedPriceRef = useRef(null);
    
    // Function to obtain ETH price in USD (simplified from web3service)
    const getEthPrice = async () => {
        try {
            // Try our internal API
            const response = await fetch('/api/prices', {
                headers: { 'Cache-Control': 'max-age=30' }
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data?.eth?.usd) return parseFloat(data.eth.usd);
                if (data?.eth?.coingecko) return parseFloat(data.eth.coingecko);
                if (data?.eth?.cryptocompare) return parseFloat(data.eth.cryptocompare);
            }
        } catch (error) {
            console.warn('API price fetch failed, trying fallback');
        }
        
        // Fallback: CoinGecko
        try {
            const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd');
            if (response.ok) {
                const data = await response.json();
                if (data.ethereum?.usd) {
                    return parseFloat(data.ethereum.usd);
                }
            }
        } catch (error) {
            // Continue with next fallback
        }
        
        // Fallback: Binance
        try {
            const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=ETHUSDT');
            if (response.ok) {
                const data = await response.json();
                if (data.price) {
                    return parseFloat(data.price);
                }
            }
        } catch (error) {
            console.error('All price APIs failed', { context: 'getEthPrice' });
        }
        
        // Last resort: reasonable approximation
        return 3000; // Recent reasonable approximation
    };
    
    useEffect(() => {
        let isMounted = true;
        
        const fetchPrice = async () => {
            // Use cache if less than 60 seconds old
            const now = Date.now();
            if (now - cacheTimeRef.current < 360000 && cachedPriceRef.current) {
                if (isMounted && isFirstLoad) {
                    setPrice(cachedPriceRef.current);
                    setIsFirstLoad(false);
                }
                return;
            }
            
            // Avoid concurrent calls
            if (fetchingRef.current) return;
            
            try {
                fetchingRef.current = true;
                
                // 1. Get ETH price in USD
                const ethPrice = await getEthPrice();
                
                // 2. Configure provider
                const rpcUrl = NETWORK_CONFIG.FALLBACK_RPC_URLS?.[0] || 
                              'https://eth-mainnet.g.alchemy.com/v2/lr9JPx6sJhRWwTYZngq-gJwUc-rEpB_I';
                              
                const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
                
                // 3. Create Uniswap pair contract
                const pairABI = [
                    'function getReserves() view returns (uint112, uint112, uint32)',
                    'function token0() view returns (address)'
                ];
                
                const pairContract = new ethers.Contract(
                    UNISWAP_PAIR_ADDRESS,
                    pairABI,
                    provider
                );
                
                // 4. Get reserves
                const [reserves, token0] = await Promise.all([
                    pairContract.getReserves(),
                    pairContract.token0()
                ]);
                
                // 5. Calculate price
                const isWethToken0 = token0.toLowerCase() === WETH_ADDRESS.toLowerCase();
                const [tokenReserve, ethReserve] = isWethToken0 
                    ? [reserves[1], reserves[0]]
                    : [reserves[0], reserves[1]];
                
                if (!tokenReserve.isZero()) {
                    const priceInEth = ethReserve.mul(ethers.constants.WeiPerEther).div(tokenReserve);
                    const tokenPriceUsd = Number(ethers.utils.formatEther(priceInEth)) * ethPrice;
                    
                    if (isMounted) {
                        // Format with 3 decimals to match Home.js
                        const formattedPrice = tokenPriceUsd.toFixed(3);
                        setPrice(formattedPrice);
                        setIsFirstLoad(false);
                        
                        // Update cache
                        cachedPriceRef.current = formattedPrice;
                        cacheTimeRef.current = now;
                    }
                }
            } catch (error) {
                console.error('Error fetching price:', error);
            } finally {
                fetchingRef.current = false;
            }
        };
        
        fetchPrice();
        
        // Less frequent interval (60s)
        const interval = setInterval(fetchPrice, 360000);
        
        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [isFirstLoad]);
    
    // Show placeholder during initial loading
    if (isFirstLoad) {
        return <span className="price-display loading">Loading...</span>;
    }
    
    return (
        <span className="price-display">
            ${price}
        </span>
    );
};

export default React.memo(TokenPrice);
